<template>
  <b-card no-body class="mb-3 permissions-module">
    <b-card-header
      header-tag="header"
      class="module-header d-flex justify-content-between align-items-center"
    >
      <button
        type="button"
        class="module-toggle"
        v-b-toggle="`permissions_module_${module.alias}`"
      >
        <b-icon icon="chevron-right" class="when-closed" font-scale="0.8" />
        <b-icon icon="chevron-down" class="when-open" font-scale="0.8" />
        <strong>{{ module.name }}</strong>
        <b-badge variant="light" class="ml-2">
          {{ checkedCount }}/{{ allPermissions.length }} attivi
        </b-badge>
      </button>
      <div class="d-flex flex-nowrap">
        <b-button
          size="sm"
          variant="outline-secondary"
          class="mr-1"
          :disabled="isAll(visiblePermissions)"
          @click="toggle(visiblePermissions, true)"
          >Tutti</b-button
        >
        <b-button
          size="sm"
          variant="outline-secondary"
          :disabled="!visiblePermissions.some((p) => p.check)"
          @click="toggle(visiblePermissions, false)"
          >Nessuno</b-button
        >
      </div>
    </b-card-header>
    <b-collapse :id="`permissions_module_${module.alias}`" visible>
        <table class="table table-sm mb-0 permissions-table">
          <!-- larghezze fisse e colonna "Altro" sempre presente: le tabelle
               dei vari moduli restano allineate fra loro -->
          <colgroup>
            <col />
            <col v-for="action in columns" :key="action" class="verb-col" />
            <col class="extra-col" />
          </colgroup>
          <thead>
            <tr>
              <th class="resource-col">Risorsa</th>
              <th
                v-for="action in columns"
                :key="action"
                class="text-center"
              >
                {{ permissionsDictionary(action) }}
              </th>
              <th>Altro</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="resource in resources" :key="resource.name">
              <td class="resource-col">
                <b-form-checkbox
                  :checked="isAll(resource.permissions)"
                  :indeterminate="isSome(resource.permissions)"
                  :disabled="isAnyPending(resource.permissions)"
                  @change="toggle(resource.permissions, $event)"
                >
                  {{ resourcesDictionary(resource.name) }}
                  <b-badge
                    v-if="!hasResourceLabel(resource.name)"
                    variant="warning"
                    class="ml-1"
                    >senza etichetta</b-badge
                  >
                </b-form-checkbox>
              </td>
              <td
                v-for="action in columns"
                :key="action"
                class="text-center"
              >
                <template v-if="cell(resource, action)">
                  <b-spinner v-if="isPending(cell(resource, action))" small />
                  <b-form-checkbox
                    v-else
                    class="d-inline-block"
                    :checked="cell(resource, action).check"
                    :title="showPermissionInfo(action)"
                    @change="toggle([cell(resource, action)], $event)"
                  />
                </template>
                <span v-else class="text-muted">—</span>
              </td>
              <td>
                <b-badge
                  v-for="permission in extras(resource)"
                  :key="permission.id"
                  pill
                  href="#"
                  class="mr-1 extra-badge"
                  :variant="permission.check ? 'primary' : 'light'"
                  :title="showPermissionInfo(permission.action)"
                  @click.prevent="toggle([permission], !permission.check)"
                >
                  <b-spinner v-if="isPending(permission)" small />
                  {{ permissionsDictionary(permission.action) }}
                  <template v-if="!hasPermissionLabel(permission.action)">
                    (senza etichetta)</template
                  >
                </b-badge>
              </td>
            </tr>
          </tbody>
        </table>
    </b-collapse>
  </b-card>
</template>

<script>
import UserPermissionsMixin from "@/mixins/UserPermissionsMixin";

/* verbi con una colonna propria, nell'ordine in cui compaiono */
const COLUMNS = ["index", "show", "store", "update", "destroy"];

export default {
  mixins: [UserPermissionsMixin],
  name: "PermissionsModuleTable",
  props: {
    module: { type: Object, required: true },
    /* risorse da mostrare (già filtrate dalla ricerca) */
    resources: { type: Array, required: true },
  },
  data() {
    return {
      columns: COLUMNS,
      pending: {},
    };
  },
  computed: {
    allPermissions() {
      return this.module.resources.flatMap((resource) => resource.permissions);
    },
    /* le azioni di modulo valgono solo sulle righe visibili (ricerca) */
    visiblePermissions() {
      return this.resources.flatMap((resource) => resource.permissions);
    },
    /* risorsa → verbo → permesso, per non ripetere le find a ogni cella */
    cells() {
      const cells = {};
      this.resources.forEach((resource) => {
        cells[resource.name] = {};
        resource.permissions.forEach((permission) => {
          cells[resource.name][permission.action] = permission;
        });
      });
      return cells;
    },
    checkedCount() {
      return this.allPermissions.filter((permission) => permission.check)
        .length;
    },
  },
  methods: {
    cell(resource, action) {
      return this.cells[resource.name][action];
    },
    extras(resource) {
      return resource.permissions.filter(
        (permission) => !COLUMNS.includes(permission.action)
      );
    },
    isAll(permissions) {
      return (
        permissions.length > 0 &&
        permissions.every((permission) => permission.check)
      );
    },
    isSome(permissions) {
      return !this.isAll(permissions) && permissions.some((p) => p.check);
    },
    isPending(permission) {
      return !!this.pending[permission.id];
    },
    /* finché una chiamata è in corso la spunta aggregata resta bloccata:
     * un secondo clic verrebbe ignorato da toggle() lasciando la spunta
     * disallineata dai dati */
    isAnyPending(permissions) {
      return permissions.some((permission) => this.isPending(permission));
    },
    /*
     * Porta i permessi indicati al valore richiesto, una chiamata per
     * permesso; in caso di errore il permesso torna al valore precedente.
     */
    toggle(permissions, value) {
      const targets = permissions.filter(
        (permission) =>
          permission.check !== value && !this.isPending(permission)
      );
      if (!targets.length) return;

      const calls = targets.map((permission) => {
        permission.check = value;
        this.$set(this.pending, permission.id, true);
        return (value
          ? this.addPermission(permission)
          : this.deletePermission(permission)
        )
          .catch((error) => {
            permission.check = !value;
            throw error;
          })
          .finally(() => {
            this.$delete(this.pending, permission.id);
          });
      });

      Promise.allSettled(calls).then((results) => {
        const failed = results.find((result) => result.status === "rejected");
        if (failed) {
          this.$showSnackbar({
            preset: "error",
            text: `${this.$getErrorMessage(failed.reason)}`,
          });
        } else {
          this.$showSnackbar({
            preset: "success",
            text: "Permessi aggiornati",
          });
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.module-toggle {
  background: none;
  border: 0;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  &.collapsed .when-open,
  &.not-collapsed .when-closed {
    display: none;
  }
}
.permissions-table {
  /* sticky sotto ricerca e barra del modulo: gli offset li misura
   * PermissionsMatrix. Niente wrapper con overflow attorno alla tabella,
   * altrimenti lo sticky si riferirebbe a quello e non alla pagina */
  thead th {
    position: sticky;
    top: var(--table-head-top, 0);
    /* sopra le checkbox delle righe (.custom-control ha z-index 1) */
    z-index: 2;
    background-color: var(--gray-50);
    font-size: 0.75rem;
    font-weight: 600;
    vertical-align: bottom;
    white-space: nowrap;
  }
  table-layout: fixed;
  .verb-col {
    width: 110px;
  }
  .extra-col {
    width: 240px;
  }
  /* la tabella non tocca i bordi della card */
  th:first-child,
  td:first-child {
    padding-left: 1.25rem;
  }
  th:last-child,
  td:last-child {
    padding-right: 1.25rem;
  }
  td {
    vertical-align: middle;
  }
}
.extra-badge {
  cursor: pointer;
}
</style>
